import React, { Dispatch, useRef } from 'react';
import _isEmpty from 'lodash/isEmpty';
import { Global, css } from '@emotion/react';
import insertInArrayIf from '@zola-helpers/client/dist/es/util/insertInArrayIf';

import { useOnboardingContext } from '../../../context';
import useSignupHelpers from '../../../util/useSignupHelpers';
import OnboardSection from '../../../OnboardSection';
import type { GenericOnboardingApiCall } from '../../../types';

import type { OnboardStepProps } from '../../../OnboardStep';
import AccountDetailsStep from '../../steps/AccountDetailsStep';
import NamesAndDateStep from '../../steps/NamesAndDateStep';
import PhaseOfPlanningStep from '../../steps/PhaseOfPlanningStep';

import { MEDIA_QUERY, ZINDEX } from '../../../../../styles/emotion';

type CoreAccountDetailsSectionProps = {
  appDispatch: Dispatch<unknown>;
  onAccountCreation?: () => void;
  accountDetailsHed?: OnboardStepProps['hed'];
  accountDetailsDek?: OnboardStepProps['dek'];
  portalContent?: OnboardStepProps['portalContent'];
};

const CoreAccountDetailsSection: React.FC<CoreAccountDetailsSectionProps> = ({
  onAccountCreation,
  appDispatch,
  accountDetailsHed,
  accountDetailsDek,
  portalContent,
}) => {
  const { onboardingState } = useOnboardingContext();
  const { initialUserContext } = onboardingState;
  const isExistingAccount = !_isEmpty(initialUserContext) && !initialUserContext.is_guest;
  const ref = useRef<HTMLDivElement | null>(null);
  const onSignup = useSignupHelpers({
    onAccountCreation,
    appDispatch,
    recaptchaContainer: ref.current,
  });

  const recaptchaBadge = css`
    ${MEDIA_QUERY.MOBILE} {
      .grecaptcha-badge {
        position: relative;
        top: -75px !important;
        margin: 0 auto;
        z-index: ${ZINDEX.behind};
      }
    }
  `;

  /**
   * NOTE we can not do conditional rendering for the AccountDetails Step here because Onboard Section uses React.Children internally
   * @see https://reactjs.org/docs/react-api.html#reactchildren
   */
  const steps = [
    <PhaseOfPlanningStep portalContent={portalContent} key="phase" />,
    <NamesAndDateStep portalContent={portalContent} key="names" />,
    ...insertInArrayIf(
      !isExistingAccount,
      <AccountDetailsStep
        key="account"
        hed={accountDetailsHed}
        dek={accountDetailsDek}
        portalContent={portalContent}
      />
    ),
  ];
  return (
    <div ref={ref}>
      <Global styles={recaptchaBadge} />
      <OnboardSection onFormSubmit={(onSignup as unknown) as GenericOnboardingApiCall}>
        {steps}
      </OnboardSection>
    </div>
  );
};

export default CoreAccountDetailsSection;
